import { UserRole } from '@/types';

/**
 * Permission system for role-based access control (RBAC)
 */

export enum Permission {
  // Job Management
  VIEW_JOBS = 'view_jobs',
  CREATE_JOB = 'create_job',
  EDIT_JOB = 'edit_job',
  DELETE_JOB = 'delete_job',
  PUBLISH_JOB = 'publish_job',
  
  // Application Management
  VIEW_APPLICATIONS = 'view_applications',
  REVIEW_APPLICATION = 'review_application',
  SHORTLIST_CANDIDATE = 'shortlist_candidate',
  REJECT_CANDIDATE = 'reject_candidate',
  
  // Interview Management
  SCHEDULE_INTERVIEW = 'schedule_interview',
  CONDUCT_INTERVIEW = 'conduct_interview',
  VIEW_INTERVIEW_FEEDBACK = 'view_interview_feedback',
  
  // Offer Management
  CREATE_OFFER = 'create_offer',
  APPROVE_OFFER = 'approve_offer',
  SEND_OFFER = 'send_offer',
  
  // Candidate Management
  VIEW_CANDIDATE_PROFILE = 'view_candidate_profile',
  VIEW_CANDIDATE_RESUME = 'view_candidate_resume',
  CONTACT_CANDIDATE = 'contact_candidate',
  ADD_CANDIDATE_NOTE = 'add_candidate_note',
  
  // User Management
  VIEW_USERS = 'view_users',
  INVITE_USER = 'invite_user',
  EDIT_USER = 'edit_user',
  DELETE_USER = 'delete_user',
  ASSIGN_ROLE = 'assign_role',
  
  // Organization Management
  VIEW_ORG_SETTINGS = 'view_org_settings',
  EDIT_ORG_SETTINGS = 'edit_org_settings',
  VIEW_BILLING = 'view_billing',
  MANAGE_BILLING = 'manage_billing',
  VIEW_ORG_ANALYTICS = 'view_org_analytics',
  
  // Compliance & Reporting
  VIEW_COMPLIANCE = 'view_compliance',
  MANAGE_COMPLIANCE = 'manage_compliance',
  EXPORT_DATA = 'export_data',
  VIEW_AUDIT_LOG = 'view_audit_log',
  
  // Platform Administration (Super Admin only)
  VIEW_ALL_ORGS = 'view_all_orgs',
  MANAGE_ALL_ORGS = 'manage_all_orgs',
  VIEW_SYSTEM_LOGS = 'view_system_logs',
  MANAGE_SYSTEM_SETTINGS = 'manage_system_settings',
  
  // Profile Management (Self)
  VIEW_OWN_PROFILE = 'view_own_profile',
  EDIT_OWN_PROFILE = 'edit_own_profile',
  APPLY_TO_JOB = 'apply_to_job',
}

/**
 * Role-based permission matrix
 */
const rolePermissions: Record<UserRole, Permission[]> = {
  candidate: [
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.APPLY_TO_JOB,
    Permission.VIEW_JOBS,
  ],
  
  recruiter: [
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.VIEW_JOBS,
    Permission.CREATE_JOB,
    Permission.EDIT_JOB,
    Permission.VIEW_APPLICATIONS,
    Permission.REVIEW_APPLICATION,
    Permission.SHORTLIST_CANDIDATE,
    Permission.REJECT_CANDIDATE,
    Permission.VIEW_CANDIDATE_PROFILE,
    Permission.VIEW_CANDIDATE_RESUME,
    Permission.CONTACT_CANDIDATE,
    Permission.ADD_CANDIDATE_NOTE,
    Permission.SCHEDULE_INTERVIEW,
  ],
  
  hiring_manager: [
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.VIEW_JOBS,
    Permission.CREATE_JOB,
    Permission.EDIT_JOB,
    Permission.PUBLISH_JOB,
    Permission.VIEW_APPLICATIONS,
    Permission.REVIEW_APPLICATION,
    Permission.SHORTLIST_CANDIDATE,
    Permission.REJECT_CANDIDATE,
    Permission.VIEW_CANDIDATE_PROFILE,
    Permission.VIEW_CANDIDATE_RESUME,
    Permission.SCHEDULE_INTERVIEW,
    Permission.CONDUCT_INTERVIEW,
    Permission.VIEW_INTERVIEW_FEEDBACK,
    Permission.CREATE_OFFER,
    Permission.SEND_OFFER,
    Permission.ADD_CANDIDATE_NOTE,
  ],
  
  hr_admin: [
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.VIEW_JOBS,
    Permission.CREATE_JOB,
    Permission.EDIT_JOB,
    Permission.DELETE_JOB,
    Permission.PUBLISH_JOB,
    Permission.VIEW_APPLICATIONS,
    Permission.REVIEW_APPLICATION,
    Permission.SHORTLIST_CANDIDATE,
    Permission.REJECT_CANDIDATE,
    Permission.VIEW_CANDIDATE_PROFILE,
    Permission.VIEW_CANDIDATE_RESUME,
    Permission.CONTACT_CANDIDATE,
    Permission.ADD_CANDIDATE_NOTE,
    Permission.SCHEDULE_INTERVIEW,
    Permission.CONDUCT_INTERVIEW,
    Permission.VIEW_INTERVIEW_FEEDBACK,
    Permission.CREATE_OFFER,
    Permission.APPROVE_OFFER,
    Permission.SEND_OFFER,
    Permission.VIEW_USERS,
    Permission.INVITE_USER,
    Permission.EDIT_USER,
    Permission.VIEW_ORG_SETTINGS,
    Permission.VIEW_COMPLIANCE,
    Permission.MANAGE_COMPLIANCE,
    Permission.EXPORT_DATA,
    Permission.VIEW_AUDIT_LOG,
    Permission.VIEW_ORG_ANALYTICS,
  ],
  
  org_owner: [
    // All HR Admin permissions plus:
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.VIEW_JOBS,
    Permission.CREATE_JOB,
    Permission.EDIT_JOB,
    Permission.DELETE_JOB,
    Permission.PUBLISH_JOB,
    Permission.VIEW_APPLICATIONS,
    Permission.REVIEW_APPLICATION,
    Permission.SHORTLIST_CANDIDATE,
    Permission.REJECT_CANDIDATE,
    Permission.VIEW_CANDIDATE_PROFILE,
    Permission.VIEW_CANDIDATE_RESUME,
    Permission.CONTACT_CANDIDATE,
    Permission.ADD_CANDIDATE_NOTE,
    Permission.SCHEDULE_INTERVIEW,
    Permission.CONDUCT_INTERVIEW,
    Permission.VIEW_INTERVIEW_FEEDBACK,
    Permission.CREATE_OFFER,
    Permission.APPROVE_OFFER,
    Permission.SEND_OFFER,
    Permission.VIEW_USERS,
    Permission.INVITE_USER,
    Permission.EDIT_USER,
    Permission.DELETE_USER,
    Permission.ASSIGN_ROLE,
    Permission.VIEW_ORG_SETTINGS,
    Permission.EDIT_ORG_SETTINGS,
    Permission.VIEW_BILLING,
    Permission.MANAGE_BILLING,
    Permission.VIEW_ORG_ANALYTICS,
    Permission.VIEW_COMPLIANCE,
    Permission.MANAGE_COMPLIANCE,
    Permission.EXPORT_DATA,
    Permission.VIEW_AUDIT_LOG,
  ],
  
  super_admin: Object.values(Permission), // All permissions
};

/**
 * Check if a role has a specific permission
 */
export const hasPermission = (role: UserRole, permission: Permission): boolean => {
  const permissions = rolePermissions[role];
  return permissions ? permissions.includes(permission) : false;
};

/**
 * Check if a role has any of the specified permissions
 */
export const hasAnyPermission = (role: UserRole, permissions: Permission[]): boolean => {
  return permissions.some(permission => hasPermission(role, permission));
};

/**
 * Check if a role has all of the specified permissions
 */
export const hasAllPermissions = (role: UserRole, permissions: Permission[]): boolean => {
  return permissions.every(permission => hasPermission(role, permission));
};

/**
 * Get all permissions for a role
 */
export const getRolePermissions = (role: UserRole): Permission[] => {
  return rolePermissions[role] || [];
};

/**
 * Check if user can access organization features
 */
export const isOrganizationUser = (role: UserRole): boolean => {
  return ['recruiter', 'hiring_manager', 'hr_admin', 'org_owner'].includes(role);
};

/**
 * Check if user has admin privileges within their organization
 */
export const isOrgAdmin = (role: UserRole): boolean => {
  return ['hr_admin', 'org_owner'].includes(role);
};

/**
 * Check if user is platform administrator
 */
export const isPlatformAdmin = (role: UserRole): boolean => {
  return role === 'super_admin';
};

/**
 * Check if user can manage team members
 */
export const canManageTeam = (role: UserRole): boolean => {
  return hasPermission(role, Permission.INVITE_USER) || 
         hasPermission(role, Permission.DELETE_USER);
};

/**
 * Check if user can manage jobs
 */
export const canManageJobs = (role: UserRole): boolean => {
  return hasPermission(role, Permission.CREATE_JOB) || 
         hasPermission(role, Permission.EDIT_JOB);
};

/**
 * Check if user can review applications
 */
export const canReviewApplications = (role: UserRole): boolean => {
  return hasPermission(role, Permission.REVIEW_APPLICATION);
};

/**
 * Route access control
 */
export interface RouteAccess {
  path: string;
  allowedRoles: UserRole[];
  requiredPermissions?: Permission[];
}

export const protectedRoutes: RouteAccess[] = [
  // Dashboard routes
  { path: '/dashboard', allowedRoles: ['candidate', 'recruiter', 'hiring_manager', 'hr_admin', 'org_owner', 'super_admin'] },
  
  // Profile routes (all authenticated users)
  { path: '/profile', allowedRoles: ['candidate', 'recruiter', 'hiring_manager', 'hr_admin', 'org_owner', 'super_admin'] },
  { path: '/profile/edit', allowedRoles: ['candidate', 'recruiter', 'hiring_manager', 'hr_admin', 'org_owner', 'super_admin'] },
  
  // Job routes
  { path: '/jobs/create', allowedRoles: ['recruiter', 'hiring_manager', 'hr_admin', 'org_owner'], requiredPermissions: [Permission.CREATE_JOB] },
  { path: '/jobs/:id/edit', allowedRoles: ['recruiter', 'hiring_manager', 'hr_admin', 'org_owner'], requiredPermissions: [Permission.EDIT_JOB] },
  
  // Application routes
  { path: '/applications', allowedRoles: ['candidate'] },
  { path: '/applications/:id', allowedRoles: ['candidate', 'recruiter', 'hiring_manager', 'hr_admin', 'org_owner'] },
  
  // Organization routes (organization users only)
  { path: '/organization', allowedRoles: ['recruiter', 'hiring_manager', 'hr_admin', 'org_owner'] },
  { path: '/organization/settings', allowedRoles: ['hr_admin', 'org_owner'], requiredPermissions: [Permission.VIEW_ORG_SETTINGS] },
  { path: '/organization/team', allowedRoles: ['hr_admin', 'org_owner'], requiredPermissions: [Permission.VIEW_USERS] },
  { path: '/organization/billing', allowedRoles: ['org_owner'], requiredPermissions: [Permission.VIEW_BILLING] },
  
  // Admin routes (platform administrators only)
  { path: '/admin', allowedRoles: ['super_admin'] },
  { path: '/admin/users', allowedRoles: ['super_admin'], requiredPermissions: [Permission.VIEW_ALL_ORGS] },
  { path: '/admin/organizations', allowedRoles: ['super_admin'], requiredPermissions: [Permission.MANAGE_ALL_ORGS] },
];

/**
 * Check if user can access a specific route
 */
export const canAccessRoute = (
  path: string, 
  userRole: UserRole, 
  _userPermissions?: Permission[]
): boolean => {
  const route = protectedRoutes.find(r => {
    // Simple path matching (could be enhanced with path-to-regexp)
    return path.startsWith(r.path.split(':')[0]);
  });
  
  if (!route) {
    // Route not in protected list, allow access
    return true;
  }
  
  // Check if user's role is allowed
  if (!route.allowedRoles.includes(userRole)) {
    return false;
  }
  
  // Check if route requires specific permissions
  if (route.requiredPermissions && route.requiredPermissions.length > 0) {
    return hasAllPermissions(userRole, route.requiredPermissions);
  }
  
  return true;
};
